import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const AdminPartnerDashboard = () => {
    const [partners, setPartners] = useState([]);
    const toast = useToast();
    const [rejectionModal, setRejectionModal] = useState({ open: false, partnerId: null, reason: '' });
    const [previewModal, setPreviewModal] = useState({ open: false, partner: null, courses: [] });
    const [editModal, setEditModal] = useState({ open: false, partner: null });

    const [isRequesting, setIsRequesting] = useState(false);
    const [isFetchingPartners, setIsFetchingPartners] = useState(false);
    const [isLoadingApprove, setIsLoadingApprove] = useState(false);
    const [isLoadingReject, setIsLoadingReject] = useState(false);
    const [isLoadingPreviewCourse, setIsLoadingPreviewCourse] = useState(false);
    const [isLoadingToggleCourse, setIsLoadingToggleCourse] = useState(false);
    const [isLoadingSavePartner, setIsLoadingSavePartner] = useState(false);
    const [isLoadingManageCourse, setIsLoadingManageCourse] = useState(false);


    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        setIsFetchingPartners(true)
        try {
            const res = await axiosClient.get('/partners/applications');
            setPartners(res.data.partners);
        } catch (err) {
            toast.error('Failed to load partners');
        } finally {
            setIsFetchingPartners(false)
        }
    };

    const [courseModal, setCourseModal] = useState({
        open: false,
        partnerId: null,
        partnerName: '',
        courses: [],
        removalReasons: {}
    });

    const [infoRequestModal, setInfoRequestModal] = useState({
        open: false,
        partnerId: null,
        customMessage: ''
    });

    const handleRequestInfo = async () => {
        setIsRequesting(true)
        try {
            await axiosClient.post(`/partners/${infoRequestModal.partnerId}/request-info`, {
                customMessage: infoRequestModal.customMessage
            });
            toast.success('Requirements email sent');
            setInfoRequestModal({ open: false, partnerId: null, customMessage: '' });
            fetchPartners();
        } catch (err) {
            toast.error('Failed to send request');
        } finally {
            setIsRequesting(false)
        }
    };

    const openCourseModal = async (partnerId, partnerName) => {
        setIsLoadingManageCourse(true)
        try {
            const res = await axiosClient.get(`/partners/${partnerId}/manage-courses`);
            setCourseModal({
                open: true,
                partnerId,
                partnerName,
                courses: res.data.courses,
                removalReasons: {}
            });
        } catch (err) {
            toast.error('Failed to load courses');
        } finally {
            setIsLoadingManageCourse(false)
        }
    };

    const handleToggleCourse = async (courseId, currentlyInProgram) => {
        const reason = currentlyInProgram ? courseModal.removalReasons[courseId] : '';
        if (currentlyInProgram && !reason) {
            toast.error('Please provide a reason for removal');
            return;
        }

        setIsLoadingToggleCourse(true)

        try {
            await axiosClient.post(`/partners/${courseModal.partnerId}/courses/${courseId}/toggle-admin`, {
                reason
            });
            toast.success(`Course ${currentlyInProgram ? 'removed' : 'added'} successfully`);
            setCourseModal(prev => ({
                ...prev,
                courses: prev.courses.map(c =>
                    c._id === courseId ? { ...c, isInPartnerProgram: !currentlyInProgram } : c
                )
            }));
        } catch (err) {
            toast.error('Action failed');
        } finally {
            setIsLoadingToggleCourse(false)
        }
    };

    const handleReasonChange = (courseId, reason) => {
        setCourseModal(prev => ({
            ...prev,
            removalReasons: { ...prev.removalReasons, [courseId]: reason }
        }));
    };

    const openEditModal = (partner) => {
        setEditModal({ open: true, partner });
    };

    const openPreviewModal = async (partner) => {
        setIsLoadingPreviewCourse(true)
        try {
            const coursesRes = await axiosClient.get(`/partners/${partner._id}/courses`);
            setPreviewModal({
                open: true,
                partner,
                courses: coursesRes.data.courses || []
            });
        } catch (err) {
            toast.error('Failed to load partner details');
            setPreviewModal({ open: false, partner: null, courses: [] });
        } finally {
            setIsLoadingPreviewCourse(false)
        }

    };

    const handleApprove = async (id, commissionRate) => {
        setIsLoadingApprove(true)
        try {
            await axiosClient.put(`/partners/${id}/process`, {
                status: 'approved',
                commissionRate: parseFloat(commissionRate)
            });
            toast.success('Partner approved');
            fetchPartners();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Approval failed');
        } finally {
            setIsLoadingApprove(false)
        }
    };

    const handleReject = async (id, reason) => {
        setIsLoadingReject(true)
        try {
            await axiosClient.put(`/partners/${id}/process`, {
                status: 'rejected',
                rejectionReason: reason
            });
            toast.success('Partner rejected');
            fetchPartners();
        } catch (err) {
            toast.error('Rejection failed');
        } finally {
            setIsLoadingReject(false)
        }
    };

    const handleSavePartner = async () => {
        setIsLoadingSavePartner(true)
        try {
            const payload = {
                institutionName: editModal.partner.partnerDetails?.institutionName,
                commissionRate: editModal.partner.partnerDetails?.commissionRate,
            };
            await axiosClient.put(`/partners/applications/${editModal.partner._id}/update`, payload);
            toast.success('Partner updated');
            setEditModal({ open: false, partner: null });
            fetchPartners();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Update failed');
        } finally {
            setIsLoadingSavePartner(false)
        }
    };

    if (isFetchingPartners) {
        return (
            <DashboardLayout userRole="admin">
                <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="admin">
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Partner Program Management</h1>
                    <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                        Review and manage institutional partner applications.
                    </p>
                </div>

                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                            <thead className="bg-neutral-50 dark:bg-neutral-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Institution</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Instructor</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Commission</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                                {partners.map(p => (
                                    <tr key={p._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {p.partnerDetails?.institutionLogo ? (
                                                    <img
                                                        src={`${process.env.REACT_APP_SOCKET_URL}/public${p.partnerDetails.institutionLogo}`}
                                                        alt={p.partnerDetails.institutionName}
                                                        className="h-8 w-8 object-contain rounded mr-3"
                                                    />
                                                ) : (
                                                    <div className="h-8 w-8 bg-neutral-200 dark:bg-neutral-700 rounded mr-3 flex items-center justify-center">
                                                        <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                                            {p.partnerDetails?.institutionName?.charAt(0) || 'I'}
                                                        </span>
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium text-neutral-900 dark:text-white">
                                                        {p.partnerDetails?.institutionName || '—'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                            {p.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${p.partnerStatus === 'approved'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                : p.partnerStatus === 'rejected'
                                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                                }`}>
                                                {p.partnerStatus.charAt(0).toUpperCase() + p.partnerStatus.slice(1)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                            {p.partnerDetails?.commissionRate ? `${p.partnerDetails.commissionRate}%` : '—'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex flex-col items-center gap-2">
                                                <button
                                                    onClick={() => openPreviewModal(p)}
                                                    disabled={isLoadingPreviewCourse}
                                                    className="text-green-600 hover:text-green-800 dark:text-green-400 text-sm font-medium"
                                                >
                                                    {isLoadingPreviewCourse ? 'Pleease wait...' : 'View'}
                                                </button>
                                            </div>
                                            {p.partnerStatus === 'pending' ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <input
                                                        type="number"
                                                        defaultValue={50}
                                                        min="10"
                                                        max="90"
                                                        className="w-16 px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                                        id={`commission-${p._id}`}
                                                    />
                                                    <button
                                                        onClick={() => setInfoRequestModal({ open: true, partnerId: p._id, customMessage: '' })}
                                                        disabled={isRequesting}
                                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm ml-2"
                                                    >
                                                        {isRequesting ? 'Requesting...' : 'Request Info'}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const rate = document.getElementById(`commission-${p._id}`).value;
                                                            handleApprove(p._id, rate);
                                                        }}
                                                        disabled={isLoadingApprove}
                                                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                                                    >
                                                        {isLoadingApprove ? 'Approving...' : 'Approve'}
                                                    </button>
                                                    <button
                                                        onClick={() => setRejectionModal({ open: true, partnerId: p._id, reason: '' })}
                                                        disabled={isLoadingReject}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        {isLoadingReject ? 'Rejecting...' : 'Reject'}
                                                    </button>
                                                </div>
                                            ) : p.partnerStatus === 'approved' ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <button
                                                        onClick={() => openEditModal(p)}
                                                        className="text-primary-600 hover:text-primary-800 dark:text-primary-400"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => openCourseModal(p._id, p.name)}
                                                        disabled={isLoadingManageCourse}
                                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                                    >

                                                        {isLoadingManageCourse ? 'Please wait...' : 'Manage Courses'}
                                                    </button>

                                                </div>
                                            ) : p.partnerStatus === 'documents_submitted' ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <input
                                                        type="number"
                                                        defaultValue={50}
                                                        min="10"
                                                        max="90"
                                                        className="w-16 px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                                        id={`commission-${p._id}`}
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            const rate = document.getElementById(`commission-${p._id}`).value;
                                                            handleApprove(p._id, rate);
                                                        }}
                                                        disabled={isLoadingApprove}
                                                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                                                    >
                                                        {isLoadingApprove ? 'Approving...' : 'Approve'}
                                                    </button>
                                                    <button
                                                        onClick={() => setRejectionModal({ open: true, partnerId: p._id, reason: '' })}
                                                        disabled={isLoadingReject}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        {isLoadingReject ? 'Rejecting...' : 'Reject'}
                                                    </button>
                                                </div>
                                            ) : p.partnerStatus === 'documents_resubmitted' ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <input
                                                        type="number"
                                                        defaultValue={50}
                                                        min="10"
                                                        max="90"
                                                        className="w-16 px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                                                        id={`commission-${p._id}`}
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            const rate = document.getElementById(`commission-${p._id}`).value;
                                                            handleApprove(p._id, rate);
                                                        }}
                                                        disabled={isLoadingApprove}
                                                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                                                    >
                                                        {isLoadingApprove ? 'Approving...' : 'Approve'}
                                                    </button>
                                                    <button
                                                        onClick={() => setRejectionModal({ open: true, partnerId: p._id, reason: '' })}
                                                        disabled={isLoadingReject}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        {isLoadingReject ? 'Rejecting...' : 'Reject'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-neutral-500">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Rejection Modal */}
            {rejectionModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                            Reject Partner Application
                        </h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                            Provide a clear reason. This will be emailed to the instructor.
                        </p>
                        <textarea
                            value={rejectionModal.reason}
                            onChange={(e) => setRejectionModal({ ...rejectionModal, reason: e.target.value })}
                            placeholder="e.g., Incomplete documentation, mismatched credentials..."
                            rows="4"
                            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                onClick={() => setRejectionModal({ open: false })}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    handleReject(rejectionModal.partnerId, rejectionModal.reason);
                                    setRejectionModal({ open: false });
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Partner Modal */}
            {editModal.open && editModal.partner && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                            Edit Partner Details
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Institution Name
                                </label>
                                <input
                                    type="text"
                                    value={editModal.partner.partnerDetails?.institutionName || ''}
                                    onChange={(e) =>
                                        setEditModal({
                                            ...editModal,
                                            partner: {
                                                ...editModal.partner,
                                                partnerDetails: {
                                                    ...editModal.partner.partnerDetails,
                                                    institutionName: e.target.value,
                                                },
                                            },
                                        })
                                    }
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Commission Rate (%)
                                </label>
                                <input
                                    type="number"
                                    min="10"
                                    max="90"
                                    value={editModal.partner.partnerDetails?.commissionRate || 70}
                                    onChange={(e) =>
                                        setEditModal({
                                            ...editModal,
                                            partner: {
                                                ...editModal.partner,
                                                partnerDetails: {
                                                    ...editModal.partner.partnerDetails,
                                                    commissionRate: parseInt(e.target.value) || 70,
                                                },
                                            },
                                        })
                                    }
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => setEditModal({ open: false, partner: null })}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSavePartner}
                                disabled={isLoadingSavePartner}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
                            >
                                {isLoadingSavePartner ? 'Please wait..' : ' Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Partner Preview Modal */}
            {previewModal.open && previewModal.partner && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-2xl overflow-hidden">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <div className="flex justify-between items-start">
                                <h3 className="text-xl font-bold text-neutral-900 dark:text-white">Partner Details</h3>
                                <button
                                    onClick={() => setPreviewModal({ open: false, partner: null, courses: [] })}
                                    className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">

                            <div className="flex items-center space-x-4">
                                {previewModal.partner.partnerDetails?.institutionLogo ? (
                                    <img
                                        src={`${process.env.REACT_APP_SOCKET_URL}/public${previewModal.partner.partnerDetails.institutionLogo}`}
                                        alt={previewModal.partner.partnerDetails.institutionName}
                                        className="w-16 h-16 object-contain rounded-lg border border-neutral-200 dark:border-neutral-700"
                                    />
                                ) : (
                                    <div className="w-16 h-16 rounded-lg bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                                        <span className="text-lg font-bold text-neutral-600 dark:text-neutral-400">
                                            {previewModal.partner.partnerDetails?.institutionName?.charAt(0) || 'I'}
                                        </span>
                                    </div>
                                )}
                                <div>
                                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                        {previewModal.partner.partnerDetails?.institutionName || '—'}
                                    </h4>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                        {previewModal.partner.name} • {previewModal.partner.email}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-neutral-50 dark:bg-neutral-700/30 p-4 rounded-lg">
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Status</p>
                                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mt-1 ${previewModal.partner.partnerStatus === 'approved'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                        : previewModal.partner.partnerStatus === 'rejected'
                                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                        }`}>
                                        {previewModal.partner.partnerStatus.charAt(0).toUpperCase() + previewModal.partner.partnerStatus.slice(1)}
                                    </span>
                                </div>
                                <div className="bg-neutral-50 dark:bg-neutral-700/30 p-4 rounded-lg">
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Commission Rate</p>
                                    <p className="font-medium text-neutral-900 dark:text-white mt-1">
                                        {previewModal.partner.partnerDetails?.commissionRate ? `${previewModal.partner.partnerDetails.commissionRate}%` : '—'}
                                    </p>
                                </div>
                            </div>

                            {previewModal.partner.partnerStatus === 'rejected' && previewModal.partner.partnerRejectionReason && (
                                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                    <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Rejection Reason</p>
                                    <p className="text-sm text-red-700 dark:text-red-400">{previewModal.partner.partnerRejectionReason}</p>
                                </div>
                            )}

                            {(previewModal.partner.partnerDetails?.signature || previewModal.partner.partnerDetails?.signatureName) && (
                                <div className="bg-neutral-50 dark:bg-neutral-700/30 p-4 rounded-lg">
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Certificate Signature</p>
                                    <div className="flex items-end space-x-3">
                                        {previewModal.partner.partnerDetails.signature && (
                                            <img
                                                src={`${process.env.REACT_APP_SOCKET_URL}/public${previewModal.partner.partnerDetails.signature}`}
                                                alt="Signature"
                                                className="h-8 object-contain"
                                            />
                                        )}
                                        {previewModal.partner.partnerDetails.signatureName && (
                                            <span className="text-neutral-900 dark:text-white text-sm font-medium">
                                                {previewModal.partner.partnerDetails.signatureName}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {previewModal.partner.partnerDocuments?.length > 0 && (
                                <div className="bg-neutral-50 dark:bg-neutral-700/30 p-4 rounded-lg">
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Submitted Documents</p>
                                    <ul className="space-y-1">
                                        {previewModal.partner.partnerDocuments.map((doc, i) => (
                                            <li key={i}>
                                                <a
                                                    href={`${process.env.REACT_APP_SOCKET_URL}/${doc?.path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm flex items-center gap-1"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    {doc.filename}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div>
                                <h5 className="font-medium text-neutral-900 dark:text-white mb-3">Courses in Partner Program</h5>
                                {previewModal.courses.length > 0 ? (
                                    <ul className="space-y-2">
                                        {previewModal.courses.map(course => (
                                            <li key={course._id} className="flex items-center space-x-3 p-3 bg-neutral-50 dark:bg-neutral-700/30 rounded">
                                                {course.thumbnail && (
                                                    <img src={`${process.env.REACT_APP_SOCKET_URL}${course.thumbnail}`} alt={course.title} className="w-10 h-10 object-cover rounded" />
                                                )}
                                                <span className="text-sm font-medium text-neutral-900 dark:text-white">{course.title}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400">No courses added to the Partner Program.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Course Management Modal */}
            {courseModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Manage Courses — {courseModal.partnerName}
                            </h3>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                                Toggle courses in/out of the Partner Program. Provide a reason when removing.
                            </p>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {courseModal.courses.length > 0 ? (
                                <div className="space-y-4">
                                    {courseModal.courses.map(course => (
                                        <div
                                            key={course._id}
                                            className="flex items-start justify-between p-4 bg-neutral-50 dark:bg-neutral-700/30 rounded-lg"
                                        >
                                            <div className="flex-1">
                                                <h4 className="font-medium text-neutral-900 dark:text-white">{course.title}</h4>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {course.isInPartnerProgram && (
                                                    <textarea
                                                        value={courseModal.removalReasons[course._id] || ''}
                                                        onChange={(e) => handleReasonChange(course._id, e.target.value)}
                                                        placeholder="Reason for removal..."
                                                        className="w-40 px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                        rows="2"
                                                    />
                                                )}
                                                <button
                                                    onClick={() => handleToggleCourse(course._id, course.isInPartnerProgram)}
                                                    disabled={isLoadingToggleCourse}
                                                    className={`px-3 py-1 text-xs rounded ${course.isInPartnerProgram
                                                        ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300'
                                                        : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300'
                                                        }`}
                                                >
                                                    {isLoadingToggleCourse ? 'Loading...' : course.isInPartnerProgram ? 'Remove' : 'Add'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-neutral-600 dark:text-neutral-400 text-center py-4">
                                    No approved courses found.
                                </p>
                            )}
                        </div>
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end">
                            <button
                                onClick={() => setCourseModal({ open: false, partnerId: null, partnerName: '', courses: [], removalReasons: {} })}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Request Modal */}
            {infoRequestModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Request Additional Information
                            </h3>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                                A PDF checklist will be emailed automatically. Optionally add a custom message.
                            </p>
                            <textarea
                                value={infoRequestModal.customMessage}
                                onChange={(e) => setInfoRequestModal({ ...infoRequestModal, customMessage: e.target.value })}
                                placeholder="e.g., Please clarify your institutional accreditation..."
                                rows="3"
                                className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
                            <button
                                onClick={() => setInfoRequestModal({ open: false, partnerId: null, customMessage: '' })}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRequestInfo}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
                            >
                                Send Request
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default AdminPartnerDashboard;